import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import InfoIcon from '@mui/icons-material/Info';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { dashboardTheme } from '../styles/dashboardTheme';

/**
 * OnboardingDialog Component
 * Shows onboarding prompt for new users
 * Follows the mobile app pattern
 */
const OnboardingDialog = ({ open, userName }) => {
  const navigate = useNavigate();

  const handleStartOnboarding = () => {
    // Navigate to chat with onboarding flag
    navigate('/chat?onboarding=true');
  };

  const onboardingSteps = [
    {
      icon: <PersonIcon />,
      text: 'Set up your profile',
      color: '#42a5f5'
    },
    {
      icon: <InfoIcon />,
      text: 'Understand how AIMHI works',
      color: '#66bb6a'
    },
    {
      icon: <AssignmentIcon />,
      text: 'Review terms & conditions',
      color: '#ffa726'
    },
    {
      icon: <PsychologyIcon />,
      text: 'Meet your AI therapist',
      color: dashboardTheme.colors.primary.main
    }
  ];

  return (
    <Dialog 
      open={open} 
      maxWidth="sm" 
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: '20px',
          background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
          maxHeight: '92vh'
        }
      }}
    >
      {/* The button lives in DialogActions, outside the scrolling content, so it
          is visible on any screen height (9 Sep 2026: it sat below the fold). */}
      <DialogContent sx={{ p: 3, pb: 1, textAlign: 'center' }}>
        {/* Welcome Avatar */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          mb: 2,
          position: 'relative'
        }}>
          <Avatar sx={{ 
            width: 80, 
            height: 80,
            background: dashboardTheme.colors.primary.gradient,
            fontSize: '2.5rem',
            boxShadow: dashboardTheme.shadows.colored(dashboardTheme.colors.primary.main)
          }}>
            👋
          </Avatar>
          <Box sx={{
            position: 'absolute',
            top: -10,
            right: '35%',
            width: 20,
            height: 20,
            borderRadius: '50%',
            background: '#4caf50',
            animation: 'pulse 2s infinite'
          }} />
        </Box>

        {/* Welcome Text */}
        <Typography 
          variant="h4" 
          gutterBottom 
          sx={{ 
            fontWeight: 700,
            background: dashboardTheme.colors.primary.gradient,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}
        >
          Welcome to AIMHI{userName ? `, ${userName}` : ''}!
        </Typography>

        <Typography 
          variant="body1" 
          sx={{ 
            color: '#64748b',
            mb: 2
          }}
        >
          Your mental health journey starts here
        </Typography>

        {/* Onboarding Steps */}
        <Box sx={{ 
          background: 'rgba(251, 146, 60, 0.05)',
          borderRadius: '16px',
          p: 2,
          mb: 2,
          border: '1px solid rgba(251, 146, 60, 0.1)'
        }}>
          <Typography 
            variant="subtitle2" 
            sx={{ 
              color: dashboardTheme.colors.primary.main,
              fontWeight: 600,
              mb: 2
            }}
          >
            Quick Setup Process:
          </Typography>
          
          <List dense sx={{ p: 0 }}>
            {onboardingSteps.map((step, index) => (
              <ListItem key={index} sx={{ px: 1, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Box sx={{ 
                    color: step.color,
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {step.icon}
                  </Box>
                </ListItemIcon>
                <ListItemText 
                  primary={step.text}
                  primaryTypographyProps={{
                    variant: 'body2',
                    sx: { fontWeight: 500, color: '#1a1a1a' }
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Benefits Preview */}
        <Box sx={{ mb: 2 }}>
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#94a3b8',
              display: 'block',
              mb: 1.5
            }}
          >
            After onboarding, you'll have access to:
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap',
            gap: 1,
            justifyContent: 'center'
          }}>
            {['AI Therapy Chat', 'Daily Check-ins', 'Personal Journal', 'Goal Tracking'].map((feature) => (
              <Box
                key={feature}
                sx={{
                  px: 1.5,
                  py: 0.5,
                  background: '#f8fafc',
                  borderRadius: '20px',
                  border: '1px solid #e2e8f0'
                }}
              >
                <Typography variant="caption" sx={{ color: '#475569', fontWeight: 500 }}>
                  🔒 {feature}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 1, flexDirection: 'column', alignItems: 'stretch' }}>
        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={handleStartOnboarding}
          endIcon={<ArrowForwardIcon />}
          sx={{
            background: dashboardTheme.colors.primary.gradient,
            borderRadius: '12px',
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: dashboardTheme.shadows.colored(dashboardTheme.colors.primary.main),
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: dashboardTheme.shadows.glow(dashboardTheme.colors.primary.main)
            }
          }}
        >
          Start Onboarding
        </Button>

        <Typography 
          variant="caption" 
          sx={{ 
            color: '#94a3b8',
            display: 'block',
            mt: 1.5,
            textAlign: 'center'
          }}
        >
          Takes about 5 minutes to complete
        </Typography>
      </DialogActions>
    </Dialog>
  );
};

export default OnboardingDialog;